import type {CSSProperties, MouseEvent as ReactMouseEvent, ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Checkbox} from '@Components/checkbox';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {DRAG_PLACEHOLDER_DATA_ATTRIBUTE, ITEM_ICON, ITEM_LABEL_KEY, SELECTED_ROW_DATA_ATTRIBUTE} from '../../layers-panel.types';
import {isEventFromSelectionCheckbox} from '../../layers-panel.helper';
import {useItemThumbDataURL} from '@Hooks/poster-editor/useItemThumbDataURL';
import styles from './layer-row.module.scss';
import panelStyles from '../../layers-panel.module.scss';

interface LayerRowProps {
  uid: string;
  isSelected: boolean;
  isInsideGroup?: boolean;
  isOverlay?: boolean;
  showSelectionCheckbox?: boolean;
  onClick?: (uid: string, event: ReactMouseEvent<HTMLDivElement>) => void;
  onCheckboxClick?: (uid: string) => void;
}

export function LayerRow({uid, isSelected, isInsideGroup = false, isOverlay = false, showSelectionCheckbox = false, onClick, onCheckboxClick}: LayerRowProps): ReactElement {
  const {attributes, isDragging, listeners, setNodeRef, transform, transition} = useSortable({id: uid});
  const thumbSrc = useItemThumbDataURL(uid);

  const showDropPlaceholder = isDragging && !isOverlay;
  const hideOverlayClone = isDragging && isOverlay;

  const style: CSSProperties = {
    ...(hideOverlayClone && {opacity: 0}),
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const onClickRow = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (isEventFromSelectionCheckbox(event)) {
      return;
    }
    onClick?.(uid, event);
  };

  const onClickCheckbox = (): void => {
    onCheckboxClick?.(uid);
  };

  const rowClasses = `${styles.row} flexbox flex-items-center gap-2 spacing-p-2 _cursor-pointer ${isSelected ? styles.selected : ''} ${isInsideGroup ? 'spacing-m-l-5' : ''} ${showDropPlaceholder ? panelStyles.dragging : ''}`;

  const renderPreview = (): ReactElement => {
    if (thumbSrc) {
      return <img alt="" className={styles.thumb} draggable={false} src={thumbSrc} />;
    }
    return <Icon icon={ITEM_ICON} size={IconSize.SIZE_ICON_16} className={`${styles.icon} flex-shrink-0`} />;
  };

  const renderDropPlaceholder = (): ReactElement | null => {
    if (!showDropPlaceholder) {
      return null;
    }
    return <div className={panelStyles.dropPlaceholder} />;
  };

  const renderDragHandle = (): ReactElement => {
    return <Icon icon="icon-draggable" size={IconSize.SIZE_ICON_16} className={`${styles.dragHandle} flex-shrink-0`} />;
  };

  const renderTrailingSlot = (): ReactElement | null => {
    if (showSelectionCheckbox) {
      return <Checkbox isChecked={isSelected} onClick={onClickCheckbox} className={panelStyles.selectionCheckbox} />;
    }
    return null;
  };

  return (
    <ClickableDiv
      ref={setNodeRef}
      className={rowClasses}
      style={style}
      enableFocusOutline={false}
      onClick={onClickRow}
      aria-label={window.i18next.t(ITEM_LABEL_KEY)}
      {...(showDropPlaceholder ? {[DRAG_PLACEHOLDER_DATA_ATTRIBUTE]: ''} : {})}
      {...(isSelected ? {[SELECTED_ROW_DATA_ATTRIBUTE]: ''} : {})}
      {...attributes}
      {...listeners}
    >
      {renderDropPlaceholder()}
      <div className={`${styles.handleSlot} flex-center flex-shrink-0`}>{renderDragHandle()}</div>
      <div className={`${styles.thumbSlot} flex-center flex-1`}>{renderPreview()}</div>
      <div className={`${styles.trailingSlot} flex-center flex-shrink-0`}>{renderTrailingSlot()}</div>
    </ClickableDiv>
  );
}
